import {InjectionToken, Signal} from '@angular/core';
import {Observable} from 'rxjs';
import {
  Appointment,
  AppointmentType,
  CalendarAppointment,
  CalendarAppointmentPatient,
  Patient,
  PracticeLocation,
  PracticePractitioner,
  PracticeSettings,
  QueryPatient,
  UserSettings
} from '@xmedical/core';

export type CalendarPatient = Patient | QueryPatient | CalendarAppointmentPatient;

/**
 * Data provider abstraction for the calendar library.
 * Each consuming app provides an implementation that bridges
 * the calendar to that app's specific API endpoints and stores.
 */
export interface CalendarDataProvider {
  // State signals
  practiceLocations: Signal<PracticeLocation[]>;
  practicePractitioners: Signal<PracticePractitioner[]>;
  selectedPracticeId: Signal<number | null>;
  appointmentTypes: Signal<AppointmentType[]>;
  practiceSettings: Signal<PracticeSettings | null>;
  /** Current user's personal settings. Optional — consumers outside a practice user context (e.g. location-portal) may omit. */
  userSettings?: Signal<UserSettings | null>;
  isReady: Signal<boolean>;
  /** Initial appointment type IDs to display. Empty array = show all. */
  defaultVisibleAppointmentTypeIds: Signal<number[]>;
  /** The current user's linked practitioner ID, if any. */
  linkedPracticePractitionerId?: Signal<number | null>;

  // Data operations (practice-scoped by the implementation)
  loadAppointmentsByDay(date: Date, locationId: number | null, practitionerId: number | null): Observable<CalendarAppointment[]>;
  loadAppointments(startDate: string, endDate: string, locationId: number | null, practitionerId: number | null): Observable<CalendarAppointment[]>;
  deleteAppointment(appointmentId: number): Observable<boolean>;
  updateAppointment(appointment: Appointment): Observable<boolean>;
  createAppointment(appointment: Appointment): Observable<boolean>;

  // Patient search — two-step: practice first, then org-wide
  searchPatients(query: string): Observable<QueryPatient[]>;
  searchOrganisationPatients?(query: string): Observable<QueryPatient[]>;

  // Clone a patient from another practice into the current practice. Returns new patient ID.
  clonePatient?(sourcePracticeId: number, sourcePatientId: number): Observable<number>;

  // WebSocket channel names for real-time updates
  getEventChannelNames(): string[];
}

/**
 * Action handler abstraction for app-specific navigation and actions.
 * Each consuming app provides an implementation.
 */
export interface CalendarActionHandler {
  // Route building — each app has its own calendar route prefix
  buildCalendarRoute(view: string, year: number, month: number, day: number): string;

  // Patient actions
  onViewPatientRequested(patient: CalendarPatient): void;
  /** Open the patient's clinical notes view. Optional — apps without a clinical notes page can omit. */
  onViewPatientClinicalRequested?(patient: CalendarPatient): void;
  /**
   * Request patient creation. The implementation opens a patient form
   * and resolves with the created patient (or null if cancelled).
   * If a patient is returned, the caller is responsible for linking
   * the patient to the appointment via dataProvider.updateAppointment().
   */
  onCreatePatientRequested(appointment: CalendarAppointment): Observable<QueryPatient | null>;

  // Messaging actions — optional; apps that don't support messaging can omit these
  onSendMessageRequested?(patient: CalendarPatient): void;
  onSendEmailRequested?(patient: CalendarPatient): void;
  onSendBulkMessageRequested?(patients: CalendarPatient[]): void;
  onSendBulkEmailRequested?(patients: CalendarPatient[]): void;
}

export const CALENDAR_DATA_PROVIDER = new InjectionToken<CalendarDataProvider>('CalendarDataProvider');
export const CALENDAR_ACTION_HANDLER = new InjectionToken<CalendarActionHandler>('CalendarActionHandler');

/**
 * Optional token for apps that have a tabs bar (e.g. xmedical-app).
 * When provided, the CalendarComponent adjusts its fixed positioning
 * to account for the extra height of the tabs bar.
 * Provide a Signal<boolean> that returns true when tabs are visible.
 */
export const CALENDAR_HAS_TABS = new InjectionToken<Signal<boolean>>('CalendarHasTabs');
